import type { FC, KeyboardEvent } from 'react';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { formatNumberAndCents } from '@makemydeal/dr-common-utils';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { convertToNumber } from '@makemydeal/dr-dash-store/utils/formatUtils';
import * as dealModeSelectors from '@makemydeal/dr-dash-store/selectors/dealModeSelectors';

interface Props {
    fieldName: string;
    fieldValue: number;
    index: number;
    handleBlurOrKeyDown?: (value: string | number, event: any) => void;
    errorDisplay: string;
    required: boolean;
    deleted: boolean;
}

export const IncentivesAmountField: FC<Props> = ({
    fieldName,
    index,
    handleBlurOrKeyDown,
    errorDisplay,
    required,
    deleted,
    fieldValue
}) => {
    const [amount, setAmount] = useState(formatNumberAndCents(fieldValue));

    const isOptionalDeskingMode = useSelector(dealModeSelectors.isOptionalDeskingMode);

    useEffect(() => {
        setAmount(formatNumberAndCents(fieldValue));
    }, [fieldValue]);

    const handleChange = (e: InterstateOnChangeEvent<string>) => {
        setAmount(e.target.value);
    };

    const handleBlurOrKeyDownEvent = (value: string, event: any) => {
        const fallbackValue = isOptionalDeskingMode ? 0 : '';
        if (handleBlurOrKeyDown) {
            handleBlurOrKeyDown(value.trim() ? convertToNumber(value) : fallbackValue, event);
        }
    };

    const handleBlur = (e: InterstateOnChangeEvent<string>) => {
        handleBlurOrKeyDownEvent(e.target.value, e);
        setAmount(formatNumberAndCents(e.target.value));
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleBlurOrKeyDownEvent(e.currentTarget.value, e);
            e.preventDefault();
        }
    };

    return (
        <>
            <NumericInput
                inputPrefix={'$'}
                autoInsertCommas
                allowDecimal
                decimalMaxLength={2}
                decimalMinLength={0}
                placeholder="0.00"
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                displayLabel={false}
                data-testid={`${fieldName}-${index}`}
                name={`${fieldName}-${index}`}
                onChange={handleChange}
                value={amount}
                errorMessage={errorDisplay}
                required={required && !isOptionalDeskingMode}
                disabled={deleted}
                size={'sm'}
                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
            />
        </>
    );
};
